import {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import Select from 'react-select'
import Swal from 'sweetalert2'
import {
  TInvoiceConfigResponse,
  TInvoiceConfigRequest,
} from '../../../coreGlobal/models/franchise/franchise'
import {useFormik} from 'formik'
import {franchiseApiPricingSchema} from '../core/validation/FanchiseVali'
import {updateInvoiceConfig} from '../core/_request'
import {buildUpdateInvoiceConfigRequest} from '../core/buildUpdateInvoiceConfigRequest'
import {PageOverlayer} from '../../../components/PageOverlayer'
import {ISelect} from '../../../coreGlobal/models/default'

type Props = {
  invoiceConfig: TInvoiceConfigResponse
}

export const ServiceInvoiceConfigForm = ({invoiceConfig}: Props) => {
  const [loading, setLoading] = useState(false)
  const [loadingPage, setLoadingPage] = useState(false)

  const [billingPeriodOptions, setBillingPeriodOptions] = useState<ISelect[]>([])
  const [billingPeriodValue, setBillingPeriodValue] = useState<ISelect | null>(null)

  let {id} = useParams()

  const formik = useFormik<TInvoiceConfigResponse>({
    initialValues: {
      availablePeriods: {
        monthly: '',
        bimonthly: '',
        quarterly: '',
        semiannual: '',
      },
      billingPeriod: '',
      invoiceDueDay: 1,
    },
    validationSchema: franchiseApiPricingSchema,
    onSubmit: async (values) => {
      const valuesFilter = await buildUpdateInvoiceConfigRequest(values)
      submit(valuesFilter)
    },
  })

  useEffect(() => {
    if (!invoiceConfig) return

    setLoadingPage(true)

    const options = Object.entries(invoiceConfig?.availablePeriods || {}).map(([value, label]) => ({
      value,
      label,
    }))

    setBillingPeriodOptions(options)

    const selectedOption = options.find((option) => option.value === invoiceConfig?.billingPeriod)

    setBillingPeriodValue(selectedOption || null)

    const values = {
      availablePeriods: invoiceConfig.availablePeriods,
      billingPeriod: invoiceConfig.billingPeriod ?? '',
      invoiceDueDay: invoiceConfig.invoiceDueDay ?? '',
    }

    formik.setValues(values)

    setLoadingPage(false)
  }, [invoiceConfig])

  const submit = async (valuesFilter: TInvoiceConfigRequest) => {
    Swal.fire({
      title: 'Deseja alterar as configurações das despesas?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'btn btn-danger', // Classe personalizada para o botão de confirmação
        cancelButton: 'btn btn-secondary', // Classe personalizada para o botão de cancelamento (opcional)
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true)
        try {
          await updateInvoiceConfig(String(id), valuesFilter)
          await Swal.fire({
            position: 'center',
            icon: 'success',
            title: 'Salvo com sucesso',
            showConfirmButton: false,
            timer: 1500,
          })
        } catch (error: any) {
          await Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: error.response?.data?.message,
          })
        } finally {
          setLoading(false)
        }
      }
    })
  }

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <div className='card card-custom mb-5 mb-xl-10'>
          <div className='card-header'>
            <h3 className='card-title fw-bolder m-0'>Configurações</h3>
            <div className='card-toolbar'></div>
          </div>
          {loadingPage ? (
            <PageOverlayer />
          ) : (
            <>
              <div className='card-body d-flex overflow-auto'>
                <div className='row'>
                  <div className='column col-lg-12'>
                    <label className='col-lg-12 col-form-label fw-bold fs-6'>
                      <span>Período de Faturamento</span>
                    </label>
                    <div className='col-lg-10 fv-row'>
                      <Select
                        id={`billingPeriod`}
                        className='react-select-styled react-select-solid '
                        classNamePrefix='react-select'
                        onChange={(option) => {
                          setBillingPeriodValue(option!)
                          formik.setFieldValue(`billingPeriod`, String(option?.value))
                        }}
                        options={billingPeriodOptions}
                        value={billingPeriodValue}
                        placeholder='Selecione'
                      />
                      {formik.errors?.billingPeriod ? (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>{formik.errors?.billingPeriod}</div>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className='column col-lg-12'>
                    <label className='col-lg-12 col-form-label fw-bold fs-6'>
                      <span>Período de Faturamento</span>
                    </label>
                    <div className='col-lg-12 fv-row'>
                      <input
                        type='number'
                        min={1}
                        max={31}
                        className='form-control form-control-lg form-control-solid'
                        placeholder='Informe um número de cadastro máximo por mês'
                        {...formik.getFieldProps('invoiceDueDay')}
                        value={formik.values.invoiceDueDay ?? ''}
                      />
                      {formik.errors?.invoiceDueDay ? (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>{formik.errors?.invoiceDueDay}</div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
              <div className='card-footer d-flex justify-content-end py-6 px-9'>
                <button
                  type='submit'
                  className='btn btn-danger'
                  disabled={loading || !formik.isValid}
                >
                  {!loading && 'Salvar'}
                  {loading && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      Salvando...{' '}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </form>
    </>
  )
}
